import { getAttributionData } from './attribution';

export interface Booth {
  id: string;
  dimensions: string;
  areaSqm: number;
  priceInr: number;
  status: 'available' | 'on_hold' | 'booked';
  companyId?: string | null;
  companyName?: string | null;
  lockedUntil?: string | null;
  lockedBy?: string | null;
  holdToken?: string | null;
}

export interface LockBoothResponse {
  success: boolean;
  message: string;
  booth: Booth;
  holdToken?: string;
  id?: string;
  status?: string;
  lockedUntil?: string | null;
}

export interface CreateOrderResponse {
  orderId: string;
  razorpayOrderId?: string;
  amount?: number;
  amountInr: number;
  basePriceInr?: number;
  gstAmountInr?: number;
  totalInr?: number;
  currency: string;
  keyId?: string;
  boothId: string;
  companyName: string;
  companyEmail: string;
}

export interface Attendee {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  companyName?: string | null;
  tier: 'trade_visitor' | 'conference_delegate' | 'exhibitor' | 'vip';
  qrToken?: string;
  profileToken?: string;
  collectionCode?: string | null;
  checkedInAt?: string | null;
  createdAt?: string;
  paymentStatus?: string;
  paymentMethod?: string;
  paymentReference?: string;
  amountPaid?: number;
  jobTitle?: string | null;
  industrySegment?: string | null;
  dietaryPreference?: string | null;
  zohoInvoiceId?: string | null;
  zohoInvoiceNumber?: string | null;
}

export interface Speaker {
  id: string;
  fullName: string;
  email: string;
  phone?: string | null;
  organization: string;
  designation: string;
  bio: string;
  headshotUrl?: string | null;
  linkedinUrl?: string | null;
  topicTitle: string;
  topicAbstract: string;
  track: string;
  status: 'submitted' | 'under_review' | 'approved' | 'rejected';
  isFeatured: boolean;
  stageOrHall?: string | null;
  sessionDate?: string | null;
  sessionStartTime?: string | null;
  sessionEndTime?: string | null;
  deckUrl?: string | null;
  deckFileName?: string | null;
  avRequirements?: string[];
  speakerNotes?: string | null;
  materialsUpdatedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface SpeakerApplicationPayload {
  fullName: string;
  email: string;
  phone?: string | null;
  organization: string;
  designation: string;
  bio: string;
  headshotUrl?: string | null;
  linkedinUrl?: string | null;
  topicTitle: string;
  topicAbstract: string;
  track: string;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
}

export interface UpdateSpeakerPayload {
  status?: 'submitted' | 'under_review' | 'approved' | 'rejected';
  fullName?: string;
  organization?: string;
  designation?: string;
  bio?: string;
  headshotUrl?: string | null;
  linkedinUrl?: string | null;
  topicTitle?: string;
  topicAbstract?: string;
  track?: string;
  phone?: string | null;
  email?: string;
  isFeatured?: boolean;
  stageOrHall?: string | null;
  sessionDate?: string | null;
  sessionStartTime?: string | null;
  sessionEndTime?: string | null;
}

export class ApiError extends Error {
  status: number;
  data?: any;
  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:4000').replace(/\/+$/, '');

export function loadRazorpayScript(): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof window !== 'undefined' && (window as any).Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export async function fetchBooths(status?: string): Promise<Booth[]> {
  const query = status ? `?status=${status}` : '';
  const res = await fetch(`${API_BASE}/api/booths${query}`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || `Failed to fetch booths: ${res.statusText}`, res.status, errorData);
  }
  return res.json();
}

export interface LockBoothPayload {
  companyEmail: string;
  companyName?: string;
  userId?: string;
  turnstileToken?: string;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
}

export interface HoldReservationPayload {
  boothId: string;
  companyEmail: string;
  companyName?: string;
  userId?: string;
  turnstileToken?: string;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
}

export async function holdReservation(payload: HoldReservationPayload): Promise<LockBoothResponse> {
  const attribution = getAttributionData();
  const enrichedPayload = {
    ...attribution,
    ...payload,
  };
  const res = await fetch(`${API_BASE}/api/reservations/hold`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(enrichedPayload),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    const message =
      errorData?.message ||
      (res.status === 409
        ? 'Stall was just claimed by another exhibitor.'
        : 'Failed to acquire reservation lock');
    throw new ApiError(message, res.status, errorData);
  }

  return res.json();
}

export async function lockBooth(
  id: string,
  payload: LockBoothPayload
): Promise<LockBoothResponse> {
  const attribution = getAttributionData();
  const enrichedPayload = {
    ...attribution,
    ...payload,
  };
  const res = await fetch(`${API_BASE}/api/booths/${encodeURIComponent(id)}/lock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(enrichedPayload),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    const message =
      errorData?.message ||
      (res.status === 409
        ? 'Stall was just claimed by another exhibitor.'
        : 'Failed to acquire reservation lock');
    throw new ApiError(message, res.status, errorData);
  }

  return res.json();
}

export interface Offline48hHoldPayload {
  companyEmail: string;
  companyName: string;
  contactPerson: string;
  contactPhone: string;
  gstin?: string;
  holdToken?: string | null;
  turnstileToken?: string;
}

export interface Offline48hHoldResponse {
  success: boolean;
  message: string;
  boothId: string;
  portalToken: string;
  holdExpiresAt: string;
}

export async function requestOffline48hBoothHold(
  id: string,
  payload: Offline48hHoldPayload
): Promise<Offline48hHoldResponse> {
  const res = await fetch(`${API_BASE}/api/booths/${encodeURIComponent(id)}/hold-offline-48h`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(
      errorData?.message || 'Failed to place 48-hour offline NEFT/RTGS hold',
      res.status,
      errorData
    );
  }

  return res.json();
}

export interface CreateOrderPayload {
  boothId: string;
  holdToken: string;
  companyEmail: string;
  companyName: string;
  contactPerson: string;
  contactPhone: string;
  gstin?: string;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
}

export async function createOrder(payload: CreateOrderPayload): Promise<CreateOrderResponse> {
  const attribution = getAttributionData();
  const enrichedPayload = {
    ...attribution,
    ...payload,
  };
  const res = await fetch(`${API_BASE}/api/orders/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(enrichedPayload),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to prepare payment order', res.status, errorData);
  }

  return res.json();
}

export interface VerifyBoothPaymentResponse {
  status: 'success';
  boothId: string;
  alreadyConfirmed: boolean;
  portalToken?: string;
}

/**
 * Sends Razorpay's signed proof of payment to the server, which confirms the
 * stall. The browser never marks a stall as booked by itself.
 */
export async function verifyBoothPayment(payload: {
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
}): Promise<VerifyBoothPaymentResponse> {
  const res = await fetch(`${API_BASE}/api/orders/verify-payment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to confirm stall payment', res.status, errorData);
  }

  return res.json();
}

export interface BadgeDetails {
  id?: string;
  fullName: string;
  email: string;
  phone?: string;
  companyName?: string | null;
  tier: 'trade_visitor' | 'conference_delegate' | 'exhibitor' | 'vip';
  qrToken: string;
  checkedInAt?: string | null;
  createdAt?: string;
  qrDataUrl?: string;
}

export async function fetchAttendeeBadge(qrToken: string): Promise<BadgeDetails> {
  const res = await fetch(`${API_BASE}/api/attendees/${encodeURIComponent(qrToken)}/badge`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Badge not found or invalid token', res.status, errorData);
  }
  return res.json();
}

export interface CheckInResponse {
  status: 'success';
  message: string;
  attendee: Attendee;
}

export async function checkInAttendee(qrToken: string, adminKey?: string): Promise<CheckInResponse> {
  const effectiveKey = adminKey || sessionStorage.getItem('iiab_admin_key') || '';
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (effectiveKey) {
    headers['x-admin-key'] = effectiveKey;
  }

  const res = await fetch(`${API_BASE}/api/attendees/${encodeURIComponent(qrToken)}/check-in`, {
    method: 'POST',
    headers,
  });

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    const message =
      data?.message ||
      (res.status === 409
        ? 'Duplicate scan: Attendee already checked in'
        : res.status === 402
        ? 'Admission denied: Attendee pass payment has not been completed'
        : res.status === 401
        ? 'Unauthorized: Staff credentials required to operate check-in turnstiles'
        : 'Check-in validation failed');
    throw new ApiError(message, res.status, data);
  }

  return data;
}

export interface SocialChannelMetric {
  id: string;
  platform: 'linkedin' | 'x' | 'instagram' | 'youtube' | 'facebook' | 'threads' | string;
  name: string;
  handle: string;
  followers: number;
  impressions: number;
  engagements: number;
  engagementRate: number;
  status: 'connected' | 'disconnected' | 'reauth_needed';
}

export interface SocialPost {
  id: string;
  content: string;
  platforms: string[];
  status: 'published' | 'scheduled' | 'draft';
  scheduledAt?: string;
  publishedAt?: string;
  impressions: number;
  clicks: number;
  engagements: number;
  url?: string;
}

export interface SocialPerformanceSummary {
  totalImpressions: number;
  totalReach: number;
  totalEngagements: number;
  averageEngagementRate: number;
  publishedPostsCount: number;
  scheduledPostsCount: number;
  activeChannelsCount: number;
}

export interface SocialMetrics {
  connected: boolean;
  source: 'live_api' | 'telemetry_simulation';
  studioUrl: string;
  summary: SocialPerformanceSummary;
  channels: SocialChannelMetric[];
  recentPosts: SocialPost[];
  updatedAt: string;
}

export type PostizSocialMetrics = SocialMetrics;

export interface AdminMetrics {
  revenueTotalInr: number;
  boothsBreakdown: {
    total: number;
    available: number;
    on_hold: number;
    booked: number;
  };
  attendeesBreakdown: {
    totalRegistered: number;
    totalCheckedIn: number;
    byTier: Record<string, { registered: number; checkedIn: number }>;
  };
  socialBreakdown?: SocialPerformanceSummary | null;
}

export type StaffRole = 'super_admin' | 'owner' | 'staff';

export interface AdminUser {
  id: string;
  email: string;
  name?: string | null;
  role: StaffRole;
  isBreakGlass?: boolean;
}

export interface StaffUserRecord {
  id: string;
  email: string;
  name?: string | null;
  role: StaffRole;
  isActive: boolean;
  invitedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export function getAdminHeaders(adminKey: string): Record<string, string> {
  const headers: Record<string, string> = {
    'x-admin-key': adminKey,
  };
  if (adminKey.startsWith('eyJ') || adminKey.startsWith('test_')) {
    headers['Authorization'] = `Bearer ${adminKey}`;
  }
  return headers;
}

export async function verifyAdminKeyApi(adminKey: string): Promise<{ valid: boolean; user?: AdminUser; timestamp: string }> {
  const res = await fetch(`${API_BASE}/api/admin/verify`, {
    headers: getAdminHeaders(adminKey),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Invalid administrative authorization key', res.status, errorData);
  }
  return res.json();
}

export async function fetchAdminMetrics(adminKey: string): Promise<AdminMetrics> {
  const res = await fetch(`${API_BASE}/api/admin/metrics`, {
    headers: getAdminHeaders(adminKey),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch admin metrics', res.status, errorData);
  }
  return res.json();
}

export async function fetchSocialMetricsApi(adminKey: string): Promise<SocialMetrics> {
  const res = await fetch(`${API_BASE}/api/admin/social/metrics`, {
    headers: {
      'x-admin-key': adminKey,
    },
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch social media analytics', res.status, errorData);
  }
  return res.json();
}

export interface CreateSocialPostPayload {
  content: string;
  platforms: string[];
  status?: 'published' | 'scheduled' | 'draft';
  scheduledAt?: string | null;
  url?: string | null;
  mediaUrl?: string | null;
}

export async function createSocialPostApi(
  adminKey: string,
  payload: CreateSocialPostPayload
): Promise<{ success: boolean; message: string; post: SocialPost }> {
  const res = await fetch(`${API_BASE}/api/admin/social/posts`, {
    method: 'POST',
    headers: {
      'x-admin-key': adminKey,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to compose and dispatch post', res.status, errorData);
  }
  return res.json();
}

export async function forceReleaseBooth(id: string, adminKey: string): Promise<{ success: boolean; message: string; booth: Booth }> {
  const res = await fetch(`${API_BASE}/api/admin/booths/${encodeURIComponent(id)}/force-release`, {
    method: 'POST',
    headers: {
      'x-admin-key': adminKey,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to force release booth', res.status, errorData);
  }
  return res.json();
}

export async function downloadAttendeesCsv(adminKey: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/attendees/export`, {
    headers: {
      'x-admin-key': adminKey,
    },
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to export attendee roster', res.status, errorData);
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `iiab_attendees_export_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

// -------------------------------------------------------------
// Dynamic Temporal Pricing & Paid Delegate Pass Registration
// -------------------------------------------------------------

export interface TierPrices {
  trade_visitor: number;
  conference_delegate: number;
  vip: number;
}

export interface PricingPhase {
  id: string;
  name: string;
  badge: string;
  description: string;
  startDate: string;
  endDate: string;
  prices: TierPrices;
  savingsVsSpot: TierPrices;
}

export interface PricingScheduleResponse {
  activePhase: PricingPhase;
  allPhases: PricingPhase[];
  spotPrices?: TierPrices;
  serverTime: string;
  gstPercent: number;
}

export interface AttendeeItemPayload {
  fullName: string;
  email: string;
  phone: string;
  jobTitle?: string;
  tier: 'trade_visitor' | 'conference_delegate' | 'vip';
}

export interface BookerDetailsPayload {
  fullName: string;
  email: string;
  phone: string;
  companyName?: string;
  gstin?: string;
  isAttending: boolean;
}

export interface CreateAttendeeOrderPayload {
  // Multi-attendee structure
  booker?: BookerDetailsPayload;
  attendees?: AttendeeItemPayload[];

  // Legacy single-attendee format
  fullName?: string;
  email?: string;
  phone?: string;
  companyName?: string;
  tier?: 'trade_visitor' | 'conference_delegate' | 'vip';
  gstin?: string;
  jobTitle?: string;

  // Marketing Attribution
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
}

export interface CreatedAttendeeItem {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  jobTitle?: string | null;
  tier: 'trade_visitor' | 'conference_delegate' | 'vip';
  qrToken: string;
  profileToken: string;
  basePriceInr: number;
}

export interface CreateAttendeeOrderResponse {
  orderId: string;
  razorpayOrderId: string;
  amount: number;
  amountInr: number;
  basePriceInr: number;
  gstAmountInr: number;
  currency: string;
  keyId: string;
  profileToken: string;
  qrToken: string;
  tier: string;
  phaseId: string;
  totalAttendees?: number;
  booker?: {
    fullName: string;
    email: string;
    phone: string;
    companyName?: string;
    isAttending: boolean;
  };
  attendees?: CreatedAttendeeItem[];
}

export interface RelatedAttendeeSummary {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  tier: string;
  jobTitle?: string | null;
  qrToken: string;
  profileToken: string;
  paymentStatus: string;
  profileCompleted: boolean;
  basePriceInr?: number | null;
}

export interface AttendeeProfile {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  companyName?: string | null;
  tier: string;
  qrToken?: string | null;
  paymentStatus: string;
  amountPaid?: number | null;
  basePriceInr?: number | null;
  pricingPhaseId?: string | null;
  razorpayOrderId?: string | null;
  razorpayPaymentId?: string | null;
  gstin?: string | null;
  profileCompleted: boolean;
  profileToken: string;
  jobTitle?: string | null;
  industrySegment?: string | null;
  businessNature?: string | null;
  primaryInterest?: string | null;
  dietaryPreference?: string | null;
  cityAndCountry?: string | null;
  checkedInAt?: string | null;
  createdAt?: string;
  relatedAttendees?: RelatedAttendeeSummary[];
}

export interface OrderAttendeesResponse {
  orderId: string;
  count: number;
  paymentStatus: string;
  companyName?: string | null;
  gstin?: string | null;
  totalBaseInr: number;
  totalAmountInr: number;
  attendees: AttendeeProfile[];
}

export interface UpdateProfilePayload {
  jobTitle?: string;
  industrySegment?: string;
  businessNature?: string;
  primaryInterest?: string;
  dietaryPreference?: string;
  cityAndCountry?: string;
  companyName?: string;
}

export async function fetchPricingSchedule(): Promise<PricingScheduleResponse> {
  const res = await fetch(`${API_BASE}/api/attendees/pricing`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch pricing schedule', res.status, errorData);
  }
  return res.json();
}

export async function createAttendeeOrder(payload: CreateAttendeeOrderPayload): Promise<CreateAttendeeOrderResponse> {
  const attribution = getAttributionData();
  const enrichedPayload = {
    ...attribution,
    ...payload,
  };
  const res = await fetch(`${API_BASE}/api/attendees/create-order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(enrichedPayload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to initialize registration order', res.status, errorData);
  }
  return res.json();
}

export async function fetchAttendeeProfile(token: string): Promise<AttendeeProfile> {
  const res = await fetch(`${API_BASE}/api/attendees/profile/${encodeURIComponent(token)}`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Profile not found', res.status, errorData);
  }
  return res.json();
}

export async function updateAttendeeProfile(
  token: string,
  payload: UpdateProfilePayload
): Promise<{ status: string; message: string; attendee: AttendeeProfile }> {
  const res = await fetch(`${API_BASE}/api/attendees/profile/${encodeURIComponent(token)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to update profile survey', res.status, errorData);
  }
  return res.json();
}

export async function fetchOrderAttendees(orderId: string): Promise<OrderAttendeesResponse> {
  const res = await fetch(`${API_BASE}/api/attendees/order/${encodeURIComponent(orderId)}`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to retrieve order passes', res.status, errorData);
  }
  return res.json();
}

export async function verifyAttendeePayment(payload: {
  razorpayOrderId: string;
  razorpayPaymentId?: string;
  razorpaySignature?: string;
}): Promise<{ status: string; message: string; count: number; attendees: any[] }> {
  const res = await fetch(`${API_BASE}/api/attendees/verify-payment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to verify payment', res.status, errorData);
  }
  return res.json();
}

// -------------------------------------------------------------
// Admin Offline / Manual Booking Engine
// -------------------------------------------------------------

export interface ManualReservationPayload {
  boothId: string;
  companyName: string;
  contactPerson: string;
  email: string;
  phone: string;
  gstin?: string;
  agreedAmountInr?: number;
  paymentMethod: 'razorpay' | 'neft_rtgs' | 'cheque' | 'comp_sponsor';
  paymentReference?: string;
  notes?: string;
  issueZohoInvoice?: boolean;
  bookingMode?: 'booked' | 'hold_48h';
}

export interface ManualAttendeePayload {
  fullName: string;
  email: string;
  phone: string;
  companyName?: string;
  tier: 'trade_visitor' | 'conference_delegate' | 'vip' | 'exhibitor' | 'exhibitor_pass';
  paymentMethod: 'razorpay' | 'offline' | 'exhibitor_comp' | 'neft_rtgs' | 'cheque';
  paymentReference?: string;
  notes?: string;
}

export interface AdminReservationItem {
  id: string;
  boothId: string;
  companyName: string;
  contactPerson?: string | null;
  contactEmail: string;
  contactPhone?: string | null;
  gstin?: string | null;
  agreedAmountInr?: number | null;
  status: string;
  paymentMethod: string;
  paymentReference?: string | null;
  portalToken: string;
  fasciaName?: string | null;
  compPassesClaimed: boolean;
  zohoInvoiceId?: string | null;
  zohoInvoiceNumber?: string | null;
  isOfflineHold?: boolean;
  holdHoursElapsed?: number;
  holdHoursRemaining?: number;
  holdAlertStatus?: 'active' | 'warning_36h' | 'expired_48h' | null;
  notes?: string | null;
  createdAt: string;
}

export async function createManualReservation(
  payload: ManualReservationPayload,
  adminKey: string
): Promise<{ success: boolean; message: string; reservation: AdminReservationItem; portalUrl: string }> {
  const res = await fetch(`${API_BASE}/api/admin/reservations/manual`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-admin-key': adminKey,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to process manual reservation', res.status, errorData);
  }
  return res.json();
}

export async function createManualAttendee(
  payload: ManualAttendeePayload,
  adminKey: string
): Promise<{ success: boolean; message: string; attendee: Attendee }> {
  const res = await fetch(`${API_BASE}/api/admin/attendees/manual`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-admin-key': adminKey,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to issue manual attendee pass', res.status, errorData);
  }
  return res.json();
}

export async function fetchAdminReservations(adminKey: string): Promise<AdminReservationItem[]> {
  const res = await fetch(`${API_BASE}/api/admin/reservations`, {
    headers: {
      'x-admin-key': adminKey,
    },
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch reservations', res.status, errorData);
  }
  const data = await res.json();
  return data.reservations || [];
}

export async function cancelBoothReservation(
  id: string,
  adminKey: string,
  reason?: string
): Promise<{ success: boolean; message: string; reservation: any; booth: any }> {
  const res = await fetch(`${API_BASE}/api/admin/reservations/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-admin-key': adminKey,
    },
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to cancel reservation', res.status, errorData);
  }
  return res.json();
}

export async function refundBoothReservation(
  id: string,
  adminKey: string,
  reason?: string
): Promise<{ success: boolean; message: string; reservation: any; booth: any }> {
  const res = await fetch(`${API_BASE}/api/admin/reservations/${encodeURIComponent(id)}/refund`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-admin-key': adminKey,
    },
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to refund reservation', res.status, errorData);
  }
  return res.json();
}

export async function refundAttendeePass(
  id: string,
  adminKey: string,
  reason?: string
): Promise<{ success: boolean; message: string; attendee: any }> {
  const res = await fetch(`${API_BASE}/api/admin/attendees/${encodeURIComponent(id)}/refund`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-admin-key': adminKey,
    },
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to refund attendee pass', res.status, errorData);
  }
  return res.json();
}

export async function expireStaleOrders(
  adminKey: string
): Promise<{ success: boolean; message: string; expiredAttendeesCount: number; expiredBoothOrdersCount: number }> {
  const res = await fetch(`${API_BASE}/api/admin/orders/expire-stale`, {
    method: 'POST',
    headers: {
      'x-admin-key': adminKey,
    },
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to expire stale orders', res.status, errorData);
  }
  return res.json();
}

export async function searchAdminAttendees(adminKey: string, search?: string): Promise<Attendee[]> {
  const query = search ? `?search=${encodeURIComponent(search)}` : '';
  const res = await fetch(`${API_BASE}/api/admin/attendees${query}`, {
    headers: {
      'x-admin-key': adminKey,
    },
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch attendees for helpdesk', res.status, errorData);
  }
  const data = await res.json();
  return data.attendees || [];
}

// -------------------------------------------------------------
// Post-Booking Exhibitor Portal
// -------------------------------------------------------------

export interface CompPassPayload {
  fullName: string;
  email: string;
  phone: string;
  designation?: string;
}

export interface ExhibitorDelegate {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  designation?: string | null;
  qrToken?: string | null;
  badgeCode?: string | null;
  credentialStatus?: string;
  status?: string;
  isSwapped?: boolean;
  createdAt?: string;
}

export interface ExhibitorPortalSummary {
  success: boolean;
  reservationId: string;
  boothId: string;
  boothSize: string;
  areaSqm: number;
  handoverTime: string;
  companyName: string;
  contactPerson?: string | null;
  email: string;
  phone?: string | null;
  gstin?: string | null;
  agreedAmountInr?: number | null;
  zohoInvoiceId?: string | null;
  zohoInvoiceNumber?: string | null;
  status: string;
  fasciaName?: string | null;
  directoryLogoUrl?: string | null;
  directoryBio?: string | null;
  directoryWebsite?: string | null;
  directoryCategories?: string[];
  compPassesClaimed: boolean;
  allocatedPasses?: number;
  activeClaimedCount?: number;
  swapLock?: {
    swapLockAt: string;
    isLocked: boolean;
    formattedCutoff: string;
  };
  leadsCount?: number;
  delegates: ExhibitorDelegate[];
}

export function getPortalInvoiceDownloadUrl(targetId: string, token?: string | null): string {
  const query = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${API_BASE}/api/portal/invoices/${encodeURIComponent(targetId)}/pdf${query}`;
}

export function getExhibitorLeadsCsvExportUrl(token: string): string {
  return `${API_BASE}/api/exhibitor-portal/${encodeURIComponent(token)}/leads/export`;
}

export interface ExhibitorLead {
  id: string;
  leadAttendeeId: string;
  fullName: string;
  companyName: string;
  jobTitle: string;
  tier: string;
  email: string;
  phone: string;
  isContactPrivate: boolean;
  interactionType: string;
  capturedAt: string;
  note: string;
}

export interface ExhibitorLeadsResponse {
  success: boolean;
  totalLeads: number;
  leads: ExhibitorLead[];
  isPlatformOnly: boolean;
  notice: string;
}

export interface UpdateExhibitorDetailsPayload {
  fasciaName?: string;
  directoryBio?: string;
  directoryWebsite?: string;
  directoryCategories?: string[];
  directoryLogoUrl?: string;
}

export async function fetchExhibitorPortalSummary(token: string): Promise<ExhibitorPortalSummary> {
  const res = await fetch(`${API_BASE}/api/exhibitor-portal/${encodeURIComponent(token)}`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Invalid or expired portal token', res.status, errorData);
  }
  return res.json();
}

export async function updateExhibitorPortalDetails(
  token: string,
  payload: UpdateExhibitorDetailsPayload
): Promise<{
  success: boolean;
  message: string;
  fasciaName?: string;
  directoryBio?: string;
  directoryWebsite?: string;
  directoryCategories?: string[];
  directoryLogoUrl?: string;
}> {
  const res = await fetch(`${API_BASE}/api/exhibitor-portal/${encodeURIComponent(token)}/details`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to update exhibitor details', res.status, errorData);
  }
  return res.json();
}

export async function claimExhibitorCompPasses(
  token: string,
  payload: { passes: CompPassPayload[] }
): Promise<{
  success: boolean;
  message: string;
  compPassesClaimed: boolean;
  delegates: ExhibitorDelegate[];
}> {
  const res = await fetch(`${API_BASE}/api/exhibitor-portal/${encodeURIComponent(token)}/claim-passes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to issue complimentary passes', res.status, errorData);
  }
  return res.json();
}

export async function swapExhibitorStaff(
  token: string,
  oldAttendeeId: string,
  newStaff: CompPassPayload
): Promise<{
  success: boolean;
  message: string;
  swappedOut: { id: string; fullName: string };
  newRepresentative: ExhibitorDelegate;
}> {
  const res = await fetch(`${API_BASE}/api/exhibitor-portal/${encodeURIComponent(token)}/staff/swap`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ oldAttendeeId, newStaff }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to swap representative', res.status, errorData);
  }
  return res.json();
}

export async function addExhibitorStaff(
  token: string,
  newStaff: CompPassPayload
): Promise<{
  success: boolean;
  message: string;
  representative: ExhibitorDelegate;
}> {
  const res = await fetch(`${API_BASE}/api/exhibitor-portal/${encodeURIComponent(token)}/staff`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(newStaff),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to add staff representative', res.status, errorData);
  }
  return res.json();
}

export async function fetchExhibitorLeads(token: string): Promise<ExhibitorLeadsResponse> {
  const res = await fetch(`${API_BASE}/api/exhibitor-portal/${encodeURIComponent(token)}/leads`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch exhibitor leads', res.status, errorData);
  }
  return res.json();
}

export async function updateLeadNote(
  token: string,
  interactionId: string,
  note: string
): Promise<{ success: boolean; message: string; note: string }> {
  const res = await fetch(
    `${API_BASE}/api/exhibitor-portal/${encodeURIComponent(token)}/leads/${encodeURIComponent(interactionId)}/notes`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    }
  );
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to save lead note', res.status, errorData);
  }
  return res.json();
}

export async function lookupExhibitorPortal(email: string): Promise<{ success: boolean; message: string; token?: string }> {
  const res = await fetch(`${API_BASE}/api/exhibitor-portal/lookup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Lookup request failed', res.status, errorData);
  }
  return res.json();
}

export interface AttendeeLookupResponse {
  success: boolean;
  message?: string;
  attendee?: {
    id: string;
    fullName: string;
    email: string;
    phone: string;
    companyName?: string | null;
    tier: string;
    qrToken: string | null;
    profileToken: string;
    paymentStatus: string;
    profileCompleted: boolean;
    checkedInAt?: string | null;
  };
  portalUrl?: string;
}

export async function lookupAttendeePass(query: string): Promise<AttendeeLookupResponse> {
  const res = await fetch(`${API_BASE}/api/attendees/lookup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Pass lookup failed', res.status, errorData);
  }
  return res.json();
}

// -------------------------------------------------------------
// CONFIGURATION MODULE API CALLS
// -------------------------------------------------------------

export interface StallCategoryPricing {
  category: string;
  name: string;
  dimensions: string;
  areaSqm: number;
  priceInr: number;
  inclusions: string[];
}

export interface StallPricingConfig {
  categories: StallCategoryPricing[];
  gstRatePercent: number;
  currency: string;
  updatedAt?: string;
}

export interface VisitorPricingConfig {
  phases: PricingPhase[];
  spotPrices: {
    trade_visitor: number;
    conference_delegate: number;
    vip: number;
  };
  gstRatePercent: number;
  updatedAt?: string;
}

export interface LayoutConfigResponse {
  stalls: any[];
  facilities: any[];
  pillars: any[];
  dimensions: {
    viewBox: string;
    width: number;
    height: number;
    hallBounds: { x: number; y: number; w: number; h: number };
  };
  updatedAt?: string;
}

export async function fetchAdminLayoutConfig(adminKey: string): Promise<LayoutConfigResponse> {
  const res = await fetch(`${API_BASE}/api/admin/config/layout`, {
    headers: { 'x-admin-key': adminKey },
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch layout config', res.status, errorData);
  }
  const data = await res.json();
  return data.layout;
}

export async function fetchPublicLayoutConfig(): Promise<LayoutConfigResponse> {
  const res = await fetch(`${API_BASE}/api/booths/layout`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch public layout', res.status, errorData);
  }
  return res.json();
}

export async function saveAdminLayoutConfig(layout: Partial<LayoutConfigResponse>, adminKey: string): Promise<LayoutConfigResponse> {
  const res = await fetch(`${API_BASE}/api/admin/config/layout`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'x-admin-key': adminKey,
    },
    body: JSON.stringify(layout),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to save layout config', res.status, errorData);
  }
  const data = await res.json();
  return data.layout;
}

export async function resetAdminLayoutConfig(adminKey: string): Promise<LayoutConfigResponse> {
  const res = await fetch(`${API_BASE}/api/admin/config/layout/reset`, {
    method: 'POST',
    headers: { 'x-admin-key': adminKey },
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to reset layout config', res.status, errorData);
  }
  const data = await res.json();
  return data.layout;
}

export async function fetchStallPricingConfig(adminKey: string): Promise<StallPricingConfig> {
  const res = await fetch(`${API_BASE}/api/admin/config/stall-pricing`, {
    headers: { 'x-admin-key': adminKey },
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch stall pricing', res.status, errorData);
  }
  const data = await res.json();
  return data.pricing;
}

export async function saveStallPricingConfig(
  pricing: Partial<StallPricingConfig> & { applyToBooths?: boolean },
  adminKey: string
): Promise<StallPricingConfig> {
  const res = await fetch(`${API_BASE}/api/admin/config/stall-pricing`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'x-admin-key': adminKey,
    },
    body: JSON.stringify(pricing),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to save stall pricing', res.status, errorData);
  }
  const data = await res.json();
  return data.pricing;
}

export async function updateIndividualBoothPrice(boothId: string, priceInr: number, adminKey: string): Promise<{ success: boolean; message: string }> {
  const res = await fetch(`${API_BASE}/api/admin/booths/${boothId}/price`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'x-admin-key': adminKey,
    },
    body: JSON.stringify({ priceInr }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to update booth price', res.status, errorData);
  }
  return res.json();
}

export async function fetchVisitorPricingConfig(adminKey: string): Promise<VisitorPricingConfig> {
  const res = await fetch(`${API_BASE}/api/admin/config/visitor-pricing`, {
    headers: { 'x-admin-key': adminKey },
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch visitor pricing', res.status, errorData);
  }
  const data = await res.json();
  return data.pricing;
}

export async function saveVisitorPricingConfig(pricing: Partial<VisitorPricingConfig>, adminKey: string): Promise<VisitorPricingConfig> {
  const res = await fetch(`${API_BASE}/api/admin/config/visitor-pricing`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'x-admin-key': adminKey,
    },
    body: JSON.stringify(pricing),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to save visitor pricing', res.status, errorData);
  }
  const data = await res.json();
  return data.pricing;
}

export async function resetVisitorPricingConfig(adminKey: string): Promise<VisitorPricingConfig> {
  const res = await fetch(`${API_BASE}/api/admin/config/visitor-pricing/reset`, {
    method: 'POST',
    headers: { 'x-admin-key': adminKey },
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to reset visitor pricing', res.status, errorData);
  }
  const data = await res.json();
  return data.pricing;
}

export async function applyAsSpeaker(
  payload: SpeakerApplicationPayload
): Promise<{ success: boolean; message: string; speaker: Speaker }> {
  const attribution = getAttributionData();
  const enrichedPayload = {
    ...attribution,
    ...payload,
  };
  const res = await fetch(`${API_BASE}/api/speakers/apply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(enrichedPayload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to submit speaker proposal', res.status, errorData);
  }
  return res.json();
}

export async function fetchPublicSpeakers(featuredOnly = false): Promise<Speaker[]> {
  const url = featuredOnly
    ? `${API_BASE}/api/speakers/public?featured=true`
    : `${API_BASE}/api/speakers/public`;
  const res = await fetch(url);
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch public speakers', res.status, errorData);
  }
  const data = await res.json();
  return data.speakers || [];
}

export async function fetchAdminSpeakers(adminKey: string, status?: string): Promise<Speaker[]> {
  const query = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
  const res = await fetch(`${API_BASE}/api/admin/speakers${query}`, {
    headers: { 'x-admin-key': adminKey },
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch speakers for review', res.status, errorData);
  }
  const data = await res.json();
  return data.speakers || [];
}

export async function updateAdminSpeaker(
  id: string,
  payload: UpdateSpeakerPayload,
  adminKey: string
): Promise<{ success: boolean; message: string; speaker: Speaker }> {
  const res = await fetch(`${API_BASE}/api/admin/speakers/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'x-admin-key': adminKey,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to update speaker details', res.status, errorData);
  }
  return res.json();
}

export async function approveSpeakerProposal(
  id: string,
  adminKey: string,
  details?: { stageOrHall?: string; sessionDate?: string; sessionStartTime?: string; sessionEndTime?: string }
): Promise<{ success: boolean; message: string; speaker: Speaker }> {
  const res = await fetch(`${API_BASE}/api/admin/speakers/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-admin-key': adminKey,
    },
    body: JSON.stringify(details || {}),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to approve speaker proposal', res.status, errorData);
  }
  return res.json();
}

export async function rejectSpeakerProposal(
  id: string,
  adminKey: string
): Promise<{ success: boolean; message: string; speaker: Speaker }> {
  const res = await fetch(`${API_BASE}/api/admin/speakers/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    headers: {
      'x-admin-key': adminKey,
    },
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to reject speaker proposal', res.status, errorData);
  }
  return res.json();
}

export interface BackendVersionInfo {
  service: string;
  status: 'ok' | 'offline';
  version: string;
  commit: string;
}

export async function fetchApiVersion(): Promise<BackendVersionInfo> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3500);
    const res = await fetch(`${API_BASE}/api/version`, {
      signal: controller.signal,
    }).catch(async () => {
      // Fallback to /health if /api/version is not available
      return fetch(`${API_BASE}/health`, { signal: controller.signal });
    });
    clearTimeout(timeoutId);

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const data = await res.json();
    return {
      service: data.service || 'render',
      status: 'ok',
      version: data.version || '1.0.0',
      commit: data.commit || 'unknown',
    };
  } catch {
    return {
      service: 'render',
      status: 'offline',
      version: '1.0.0',
      commit: 'unreachable',
    };
  }
}

export interface MarketingCampaignPerformance {
  campaign: string;
  conversions: number;
  revenuePaise: number;
  revenueInr: number;
}

export interface MarketingSourcePerformance {
  source: string;
  conversions: number;
  revenuePaise: number;
  revenueInr: number;
}

export interface MarketingPerformanceResponse {
  totalConversions: number;
  totalRevenuePaise: number;
  totalRevenueInr: number;
  campaigns: MarketingCampaignPerformance[];
  sources: MarketingSourcePerformance[];
  conversionTypes: Array<{
    type: string;
    count: number;
  }>;
}

export async function fetchMarketingPerformance(adminKey: string): Promise<MarketingPerformanceResponse> {
  const res = await fetch(`${API_BASE}/api/admin/marketing/performance`, {
    headers: { 'x-admin-key': adminKey },
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch marketing performance', res.status, errorData);
  }
  return res.json();
}

// ============================================================
// CRM CONTACTS & DEAL PIPELINE TYPES & API CLIENTS
// ============================================================

export interface CrmContact {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName?: string;
  jobTitle?: string;
  role: 'attendee' | 'exhibitor' | 'speaker' | 'sponsor';
  tier?: string;
  tags: string[];
  status: 'lead' | 'confirmed' | 'paid' | 'checked_in';
  source: 'direct' | 'webhook' | 'admin' | 'imported';
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, any>;
}

export interface CrmMetrics {
  totalContacts: number;
  exhibitorsCount: number;
  delegatesCount: number;
  speakersCount: number;
  tradeVisitorsCount: number;
  syncedPercentage: number;
  crmConnected: boolean;
  studioUrl: string;
}

export type TwentyContact = CrmContact;
export type TwentyCrmMetrics = CrmMetrics;

export interface FetchCrmContactsResponse {
  success: boolean;
  count: number;
  contacts: CrmContact[];
  studioUrl: string;
}

export async function fetchCrmContactsApi(
  adminKey: string,
  role?: string,
  search?: string
): Promise<FetchCrmContactsResponse> {
  const params = new URLSearchParams();
  if (role && role !== 'all') params.set('role', role);
  if (search) params.set('search', search);

  const url = `${API_BASE}/api/admin/crm/contacts${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await fetch(url, {
    headers: { 'x-admin-key': adminKey },
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch CRM contacts', res.status, errorData);
  }
  return res.json();
}

export async function fetchCrmMetricsApi(
  adminKey: string
): Promise<{ success: boolean; metrics: CrmMetrics }> {
  const res = await fetch(`${API_BASE}/api/admin/crm/metrics`, {
    headers: { 'x-admin-key': adminKey },
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch CRM metrics', res.status, errorData);
  }
  return res.json();
}

export async function createCrmContactManualApi(
  adminKey: string,
  payload: {
    fullName: string;
    email: string;
    phone?: string;
    companyName?: string;
    jobTitle?: string;
    role: 'attendee' | 'exhibitor' | 'speaker' | 'sponsor';
    tier?: string;
    tags?: string[];
  }
): Promise<{ success: boolean; result: any }> {
  const res = await fetch(`${API_BASE}/api/admin/crm/sync-manual`, {
    method: 'POST',
    headers: {
      'x-admin-key': adminKey,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to manually synchronize contact', res.status, errorData);
  }
  return res.json();
}

export interface CrmDeal {
  id: string;
  companyId?: string | null;
  contactId?: string | null;
  title: string;
  amountInr: string | number;
  stage: 'discovery' | 'stall_selected' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost';
  probability: number;
  stallId?: string | null;
  closeDate?: string | null;
  assignedTo?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export async function fetchCrmDealsApi(adminKey: string): Promise<{ success: boolean; count: number; deals: CrmDeal[] }> {
  const res = await fetch(`${API_BASE}/api/admin/crm/deals`, {
    headers: { 'x-admin-key': adminKey },
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch CRM deals', res.status, errorData);
  }
  return res.json();
}

export async function createCrmDealApi(
  adminKey: string,
  payload: {
    title: string;
    amountInr: number;
    stage?: string;
    probability?: number;
    stallId?: string;
    closeDate?: string;
    assignedTo?: string;
    contactId?: string;
  }
): Promise<{ success: boolean; deal: CrmDeal }> {
  const res = await fetch(`${API_BASE}/api/admin/crm/deals`, {
    method: 'POST',
    headers: {
      'x-admin-key': adminKey,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to create CRM deal', res.status, errorData);
  }
  return res.json();
}

export async function updateCrmDealStageApi(
  adminKey: string,
  dealId: string,
  stage: string,
  assignedTo?: string
): Promise<{ success: boolean; deal: CrmDeal }> {
  const res = await fetch(`${API_BASE}/api/admin/crm/deals/${dealId}/stage`, {
    method: 'PATCH',
    headers: {
      'x-admin-key': adminKey,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ stage, assignedTo }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to update deal stage', res.status, errorData);
  }
  return res.json();
}

// ============================================================
// CAMPAIGN WORKFLOW AUTOMATION TYPES & API CLIENTS
// ============================================================

export interface AutomationWorkflow {
  id: string;
  name: string;
  active: boolean;
  triggerType: 'webhook' | 'schedule' | 'manual';
  nodesCount: number;
  lastExecutedAt?: string;
  executionCount: number;
  tags: string[];
  description: string;
}

export interface AutomationExecution {
  id: string;
  workflowId: string;
  workflowName: string;
  campaignName: string;
  channel: 'email' | 'whatsapp' | 'omnichannel';
  segment: string;
  status: 'success' | 'running' | 'queued' | 'failed';
  dispatchedCount: number;
  startedAt: string;
  completedAt?: string;
  error?: string;
  summary?: string;
}

export type N8nWorkflow = AutomationWorkflow;
export type N8nExecution = AutomationExecution;

export interface AutomationsData {
  success: boolean;
  workflows: AutomationWorkflow[];
  executions: AutomationExecution[];
  studioUrl: string;
  webhookUrl: string;
}

export interface TriggerAutomationPayload {
  campaignName: string;
  segment: string;
  channel: 'email' | 'whatsapp' | 'omnichannel';
  subject?: string;
  messageBody: string;
  templateName?: string;
  audienceFilters?: Record<string, any>;
}

export async function fetchAutomationsApi(adminKey: string): Promise<AutomationsData> {
  const res = await fetch(`${API_BASE}/api/admin/automations`, {
    headers: { 'x-admin-key': adminKey },
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch workflow automations', res.status, errorData);
  }
  return res.json();
}

export async function triggerAutomationApi(
  adminKey: string,
  payload: TriggerAutomationPayload
): Promise<{
  success: boolean;
  executionId: string;
  dispatchedCount: number;
  channel: string;
  status: string;
  message: string;
}> {
  const res = await fetch(`${API_BASE}/api/admin/automations/trigger`, {
    method: 'POST',
    headers: {
      'x-admin-key': adminKey,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to trigger broadcast automation', res.status, errorData);
  }
  return res.json();
}

export async function fetchStaffUsers(adminKey: string): Promise<{ users: StaffUserRecord[] }> {
  const res = await fetch(`${API_BASE}/api/admin/users`, {
    headers: getAdminHeaders(adminKey),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch staff users', res.status, errorData);
  }
  return res.json();
}

export async function createStaffUser(
  adminKey: string,
  payload: { email: string; name?: string; role: StaffRole }
): Promise<{ success: boolean; user: StaffUserRecord }> {
  const res = await fetch(`${API_BASE}/api/admin/users`, {
    method: 'POST',
    headers: {
      ...getAdminHeaders(adminKey),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to create staff user', res.status, errorData);
  }
  return res.json();
}

export async function updateStaffUser(
  adminKey: string,
  id: string,
  payload: { role?: StaffRole; isActive?: boolean; name?: string }
): Promise<{ success: boolean; user: StaffUserRecord }> {
  const res = await fetch(`${API_BASE}/api/admin/users/${id}`, {
    method: 'PATCH',
    headers: {
      ...getAdminHeaders(adminKey),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to update staff user', res.status, errorData);
  }
  return res.json();
}

export async function deleteStaffUser(
  adminKey: string,
  id: string
): Promise<{ success: boolean }> {
  const res = await fetch(`${API_BASE}/api/admin/users/${id}`, {
    method: 'DELETE',
    headers: getAdminHeaders(adminKey),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to delete staff user', res.status, errorData);
  }
  return res.json();
}

// ==========================================
// 12. B2B Portals & Schedule-X Meetings
// ==========================================

export interface MeetingItem {
  id: string;
  title: string;
  start: string; // 'YYYY-MM-DD HH:mm'
  end: string;   // 'YYYY-MM-DD HH:mm'
  location: string;
  status: 'pending' | 'accepted' | 'declined' | 'cancelled';
  agenda?: string | null;
  requesterType: 'attendee' | 'exhibitor';
  requesterId: string;
  requesterName: string;
  requesterOrg?: string | null;
  targetType: 'attendee' | 'exhibitor';
  targetId: string;
  targetName: string;
  targetOrg?: string | null;
  responseMessage?: string | null;
  createdAt: string;
}

export interface RequestMeetingPayload {
  requesterType: 'attendee' | 'exhibitor';
  requesterId: string;
  requesterName: string;
  requesterOrg?: string;
  requesterEmail: string;
  targetType: 'attendee' | 'exhibitor';
  targetId: string;
  targetName: string;
  targetOrg?: string;
  targetEmail: string;
  start: string; // 'YYYY-MM-DD HH:mm'
  end: string;   // 'YYYY-MM-DD HH:mm'
  title: string;
  agenda?: string;
  locationType?: 'booth' | 'meeting_suite' | 'lounge' | 'cafe';
  locationDetails?: string;
}

export interface PortalAttendee {
  id: string;
  fullName: string;
  companyName?: string | null;
  jobTitle?: string | null;
  tier: string;
  industrySegment?: string | null;
  businessNature?: string | null;
  primaryInterest?: string | null;
  cityAndCountry?: string | null;
  bio?: string | null;
  avatarUrl?: string | null;
  linkedinUrl?: string | null;
  lookingFor?: string[] | null;
  offering?: string[] | null;
  createdAt?: string;
}

export interface PortalExhibitor {
  reservationId: string;
  boothId: string;
  companyName: string;
  fasciaName?: string | null;
  directoryLogoUrl?: string | null;
  directoryBio?: string | null;
  directoryWebsite?: string | null;
  directoryCategories?: string[] | null;
  brochureUrl?: string | null;
  videoUrl?: string | null;
  contactPerson?: string | null;
  contactEmail: string;
  boothDimensions: string;
  boothAreaSqm: number;
}

export async function fetchMeetings(
  participantId: string,
  status?: string
): Promise<{ status: string; meetings: MeetingItem[] }> {
  const query = status ? `?participantId=${encodeURIComponent(participantId)}&status=${encodeURIComponent(status)}` : `?participantId=${encodeURIComponent(participantId)}`;
  const res = await fetch(`${API_BASE}/api/meetings${query}`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch meetings', res.status, errorData);
  }
  return res.json();
}

export async function requestMeeting(
  payload: RequestMeetingPayload
): Promise<{ status: string; message: string; meeting: MeetingItem }> {
  const res = await fetch(`${API_BASE}/api/meetings/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to send meeting request', res.status, errorData);
  }
  return res.json();
}

export async function respondMeeting(
  meetingId: string,
  action: 'accept' | 'decline' | 'cancel',
  responseMessage?: string
): Promise<{ status: string; message: string; meeting: MeetingItem }> {
  const res = await fetch(`${API_BASE}/api/meetings/${encodeURIComponent(meetingId)}/respond`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, responseMessage }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to respond to meeting', res.status, errorData);
  }
  return res.json();
}

export function getMeetingIcsUrl(meetingId: string): string {
  return `${API_BASE}/api/meetings/${encodeURIComponent(meetingId)}/invite.ics`;
}

export async function fetchPortalAttendees(params?: {
  query?: string;
  industrySegment?: string;
  businessNature?: string;
}): Promise<{ status: string; attendees: PortalAttendee[] }> {
  const searchParams = new URLSearchParams();
  if (params?.query) searchParams.set('query', params.query);
  if (params?.industrySegment) searchParams.set('industrySegment', params.industrySegment);
  if (params?.businessNature) searchParams.set('businessNature', params.businessNature);
  
  const qs = searchParams.toString() ? `?${searchParams.toString()}` : '';
  const res = await fetch(`${API_BASE}/api/portal/attendees${qs}`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch attendees directory', res.status, errorData);
  }
  return res.json();
}

export async function fetchPortalExhibitors(params?: {
  category?: string;
  search?: string;
}): Promise<{ status: string; count: number; exhibitors: PortalExhibitor[] }> {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.set('category', params.category);
  if (params?.search) searchParams.set('search', params.search);
  
  const qs = searchParams.toString() ? `?${searchParams.toString()}` : '';
  const res = await fetch(`${API_BASE}/api/portal/exhibitors${qs}`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch exhibitors directory', res.status, errorData);
  }
  return res.json();
}

export async function updateAttendeeNetworkingProfile(
  token: string,
  payload: Partial<PortalAttendee & { isDirectoryVisible?: boolean }>
): Promise<{ status: string; message: string; attendee: any }> {
  const res = await fetch(`${API_BASE}/api/portal/attendees/${encodeURIComponent(token)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to update networking profile', res.status, errorData);
  }
  return res.json();
}

export interface ParticipantPortalResponse {
  success: boolean;
  attendee: Attendee & {
    primaryRole: string;
    roles: string[];
    ticketType?: string | null;
    avatarUrl?: string | null;
    linkedinUrl?: string | null;
    bio?: string | null;
    lookingFor?: string[];
    offering?: string[];
    isDirectoryVisible: boolean;
    directoryOptOut: boolean;
    shareContactOptOut: boolean;
    collectionCode?: string | null;
    badgeStatus: string;
  };
  person?: any;
  event: {
    id: string;
    name: string;
    edition?: string;
    city: string;
    venue: string;
    startDate: string;
    endDate: string;
    isLive: boolean;
  };
  roles: any[];
  activeCredential?: {
    id: string;
    badgeCode: string;
    status: string;
    issuedAt: string;
  } | null;
  eventCard: {
    eventName: string;
    dates: string;
    venue: string;
    fullName: string;
    companyName: string;
    jobTitle: string;
    photoUrl?: string | null;
    primaryRole: string;
    roles: string[];
    ticketType?: string | null;
    theme: {
      label: string;
      colorHex: string;
      badgeClass: string;
      textClass: string;
      borderClass: string;
    };
    collectionNotice: string;
    speakerSession?: any;
    exhibitorStall?: any;
  };
}

function getPortalAuthHeaders(token?: string): Record<string, string> {
  const resolvedToken =
    token ||
    (typeof window !== 'undefined' ? sessionStorage.getItem('iiab_portal_token') || undefined : undefined);
  const headers: Record<string, string> = {};
  if (resolvedToken) {
    headers['Authorization'] = `Bearer ${resolvedToken}`;
    headers['x-profile-token'] = resolvedToken;
  }
  return headers;
}

export async function getParticipantProfile(email?: string, token?: string): Promise<ParticipantPortalResponse> {
  const headers = getPortalAuthHeaders(token);
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (token) params.set('token', token);
  const query = params.toString();

  const url = query
    ? `${API_BASE}/api/portal/my-profile?${query}`
    : `${API_BASE}/api/portal/my-profile`;

  const res = await fetch(url, { headers });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch participant profile', res.status, errorData);
  }
  return res.json();
}

export async function updateParticipantProfile(payload: any, email?: string, token?: string): Promise<any> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getPortalAuthHeaders(token),
  };
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (token) params.set('token', token);
  const query = params.toString();

  const url = query
    ? `${API_BASE}/api/portal/my-profile?${query}`
    : `${API_BASE}/api/portal/my-profile`;

  const res = await fetch(url, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to update participant profile', res.status, errorData);
  }
  return res.json();
}

export async function getParticipantConnections(email?: string, token?: string): Promise<{ success: boolean; connections: any[] }> {
  const headers = getPortalAuthHeaders(token);
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (token) params.set('token', token);
  const query = params.toString();

  const url = query
    ? `${API_BASE}/api/portal/connections?${query}`
    : `${API_BASE}/api/portal/connections`;

  const res = await fetch(url, { headers });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch connections', res.status, errorData);
  }
  return res.json();
}

export async function saveConnectionNote(attendeeId: string, connectionAttendeeId: string, note: string, token?: string): Promise<any> {
  const res = await fetch(`${API_BASE}/api/portal/connections/note`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getPortalAuthHeaders(token),
    },
    body: JSON.stringify({ attendeeId, connectionAttendeeId, note }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to save note', res.status, errorData);
  }
  return res.json();
}

export async function requestSpeakerDeckUploadUrl(
  payload: { fileName: string; contentType?: string; purpose?: 'deck' | 'headshot' },
  email?: string,
  token?: string
): Promise<{
  success: boolean;
  uploadUrl?: string;
  token?: string;
  path: string;
  publicUrl: string;
  mode?: string;
}> {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (token) params.set('token', token);
  const query = params.toString();

  const res = await fetch(
    `${API_BASE}/api/portal/speaker-materials/upload-url${query ? `?${query}` : ''}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getPortalAuthHeaders(token),
      },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to prepare slide deck upload URL', res.status, errorData);
  }
  return res.json();
}

export async function updateSpeakerMaterials(
  payload: {
    deckUrl?: string | null;
    deckFileName?: string | null;
    headshotUrl?: string | null;
    avRequirements?: string[];
    speakerNotes?: string | null;
    topicTitle?: string;
    bio?: string;
  },
  email?: string,
  token?: string
): Promise<{
  success: boolean;
  speakerMaterials: {
    deckUrl?: string | null;
    deckFileName?: string | null;
    headshotUrl?: string | null;
    avRequirements?: string[];
    speakerNotes?: string | null;
    updatedAt?: string;
  };
}> {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (token) params.set('token', token);
  const query = params.toString();

  const res = await fetch(
    `${API_BASE}/api/portal/speaker-materials${query ? `?${query}` : ''}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...getPortalAuthHeaders(token),
      },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to save speaker presentation materials', res.status, errorData);
  }
  return res.json();
}

export async function fetchBookmarkedSessions(
  email?: string,
  token?: string
): Promise<{
  success: boolean;
  bookmarkedSessionIds: string[];
  sessions: any[];
}> {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (token) params.set('token', token);
  const query = params.toString();

  const res = await fetch(
    `${API_BASE}/api/portal/bookmarked-sessions${query ? `?${query}` : ''}`,
    {
      headers: getPortalAuthHeaders(token),
    }
  );
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to load bookmarked conference sessions', res.status, errorData);
  }
  return res.json();
}

export async function toggleBookmarkedSession(
  payload: {
    sessionId: string;
    action?: 'add' | 'remove' | 'toggle';
    session?: {
      id?: string;
      title?: string;
      date?: string;
      startTime?: string;
      endTime?: string;
      hall?: string;
      track?: string;
      speakerName?: string;
    };
  },
  email?: string,
  token?: string
): Promise<{
  success: boolean;
  sessionId: string;
  bookmarked: boolean;
  bookmarkedSessionIds: string[];
}> {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (token) params.set('token', token);
  const query = params.toString();

  const res = await fetch(
    `${API_BASE}/api/portal/bookmarked-sessions${query ? `?${query}` : ''}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getPortalAuthHeaders(token),
      },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to update session bookmark', res.status, errorData);
  }
  return res.json();
}

export function getPersonalScheduleIcsUrl(email?: string, token?: string): string {
  const params = new URLSearchParams();
  const effectiveToken =
    token ||
    (typeof window !== 'undefined' ? sessionStorage.getItem('iiab_portal_token') || '' : '');
  const effectiveEmail =
    email ||
    (typeof window !== 'undefined' ? sessionStorage.getItem('iiab_portal_email') || '' : '');

  if (effectiveToken) params.set('token', effectiveToken);
  if (effectiveEmail) params.set('email', effectiveEmail);
  const query = params.toString();
  return `${API_BASE}/api/portal/schedule.ics${query ? `?${query}` : ''}`;
}

export interface BadgeResolveResponse {
  authenticated: boolean;
  requiresLogin?: boolean;
  badgeCode: string;
  message?: string;
  status?: string;
  profile?: {
    id: string;
    fullName: string;
    companyName?: string;
    jobTitle?: string;
    bio?: string;
    avatarUrl?: string | null;
    linkedinUrl?: string | null;
    primaryRole: string;
    roles?: string[];
    ticketType?: string | null;
    lookingFor?: string[];
    offering?: string[];
    shareContactAllowed: boolean;
    contact?: {
      email?: string;
      phone?: string;
    } | null;
  };
}

export async function resolveBadgeScan(badgeCode: string, viewerEmail?: string, method: 'qr' | 'nfc' = 'qr'): Promise<BadgeResolveResponse> {
  const params = new URLSearchParams();
  params.set('method', method);
  if (viewerEmail) params.set('email', viewerEmail);

  const res = await fetch(`${API_BASE}/api/badges/resolve/${encodeURIComponent(badgeCode)}?${params.toString()}`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to resolve badge', res.status, errorData);
  }
  return res.json();
}

export const resolveBadgeCode = resolveBadgeScan;

export async function revealBadgeContact(badgeCode: string, viewerEmail: string): Promise<{ success: boolean; email: string; phone: string }> {
  const res = await fetch(`${API_BASE}/api/badges/reveal-contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ badgeCode, viewerEmail }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to reveal contact', res.status, errorData);
  }
  return res.json();
}

export async function getGoogleWalletPass(badgeCode: string): Promise<{ success: boolean; saveUrl: string; passData: any }> {
  const res = await fetch(`${API_BASE}/api/portal/google-wallet/${encodeURIComponent(badgeCode)}`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to generate Google Wallet pass', res.status, errorData);
  }
  return res.json();
}

export interface DeskAttendeeResult {
  attendee: Attendee & {
    primaryRole?: string;
    roles?: string[];
    ticketType?: string | null;
    badgeStatus?: string;
    collectionCode?: string | null;
  };
  roles?: Array<{
    id: string;
    role: string;
    tier: string;
    isPrimary: boolean;
    label: string;
  }>;
  activeCredential: {
    id: string;
    badgeCode: string;
    status: string;
    nfcTagId?: string | null;
    issuedAt?: string;
  } | null;
  handover: {
    id: string;
    badgeHandedOver: boolean;
    badgeHandedOverAt?: string;
    kitItems?: string[];
    handedOverBy?: string;
  } | null;
  theme: {
    role: string;
    label: string;
    colorHex: string;
    badgeClass: string;
    textClass: string;
    borderClass: string;
  };
}

export async function searchDeskAttendees(query: string, adminKey?: string): Promise<{ success: boolean; attendees: DeskAttendeeResult[] }> {
  const headers = adminKey ? getAdminHeaders(adminKey) : {};
  const res = await fetch(`${API_BASE}/api/desk/search?q=${encodeURIComponent(query)}`, { headers });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to search desk attendees', res.status, errorData);
  }
  return res.json();
}

export async function recordDeskHandover(
  payload: { attendeeId: string; delegateKitIssued?: boolean; vipKitIssued?: boolean; notes?: string },
  adminKey?: string
): Promise<{ success: boolean; handover: any }> {
  const headers = {
    'Content-Type': 'application/json',
    ...(adminKey ? getAdminHeaders(adminKey) : {}),
  };
  const res = await fetch(`${API_BASE}/api/desk/handover`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to record handover', res.status, errorData);
  }
  return res.json();
}

export async function reprintDeskBadge(
  payload: { attendeeId: string; reason: 'lost' | 'damaged' | 'spelling_fix' | 'role_change'; notes?: string },
  adminKey?: string
): Promise<{ success: boolean; newCredential: any; badgeCode: string }> {
  const headers = {
    'Content-Type': 'application/json',
    ...(adminKey ? getAdminHeaders(adminKey) : {}),
  };
  const res = await fetch(`${API_BASE}/api/desk/reprint`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to reprint badge', res.status, errorData);
  }
  return res.json();
}

export async function updateDeskAttendee(
  payload: { attendeeId: string; fullName?: string; companyName?: string; jobTitle?: string; avatarUrl?: string },
  adminKey?: string
): Promise<{ success: boolean; attendee: any }> {
  const headers = {
    'Content-Type': 'application/json',
    ...(adminKey ? getAdminHeaders(adminKey) : {}),
  };
  const res = await fetch(`${API_BASE}/api/desk/update-attendee`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to update attendee', res.status, errorData);
  }
  return res.json();
}

export async function registerWalkIn(payload: any, adminKey?: string): Promise<any> {
  const headers = {
    'Content-Type': 'application/json',
    ...(adminKey ? getAdminHeaders(adminKey) : {}),
  };
  const res = await fetch(`${API_BASE}/api/desk/walk-in`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to register walk-in attendee', res.status, errorData);
  }
  return res.json();
}

export async function scanGateTurnstile(
  payload: { code: string; location?: string; scanType?: string; eventDay?: string },
  scannerKey?: string
): Promise<any> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (scannerKey) {
    headers['x-scanner-key'] = scannerKey;
    headers['Authorization'] = `Bearer ${scannerKey}`;
  }
  const res = await fetch(`${API_BASE}/api/checkin/scan`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to scan pass', res.status, errorData);
  }
  return res.json();
}

export interface PrintBatchRecord {
  id: string;
  batchNumber: string;
  name: string;
  filterCriteria: any;
  totalCount: number;
  status: string;
  createdBy?: string;
  createdAt: string;
}

export interface Badge4x6Data {
  id: string;
  fullName: string;
  companyName?: string | null;
  jobTitle?: string | null;
  email: string;
  phone?: string;
  avatarUrl?: string | null;
  primaryRole: string;
  roles: string[];
  tier: string;
  ticketType?: string | null;
  badgeCode: string;
  nfcTagId?: string | null;
  stallNumber?: string | null;
  theme: {
    role: string;
    label: string;
    colorHex: string;
    badgeClass: string;
    textClass: string;
    borderClass: string;
  };
}

export async function fetchPrintBatches(adminKey: string): Promise<{ success: boolean; batches: PrintBatchRecord[] }> {
  const res = await fetch(`${API_BASE}/api/production/batches`, {
    headers: getAdminHeaders(adminKey),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch print batches', res.status, errorData);
  }
  return res.json();
}

export async function createPrintBatch(
  payload: { name: string; role?: string; tier?: string; company?: string; onlyUnprinted?: boolean },
  adminKey: string
): Promise<{ success: boolean; batch: PrintBatchRecord; count: number; message: string }> {
  const res = await fetch(`${API_BASE}/api/production/batches`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAdminHeaders(adminKey),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to create print batch', res.status, errorData);
  }
  return res.json();
}

export async function fetchBatchBadges(
  batchId: string,
  adminKey: string
): Promise<{ success: boolean; batch: PrintBatchRecord; badges: Badge4x6Data[] }> {
  const res = await fetch(`${API_BASE}/api/production/batches/${encodeURIComponent(batchId)}/badges`, {
    headers: getAdminHeaders(adminKey),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch batch badges', res.status, errorData);
  }
  return res.json();
}

export async function pairNfcBadge(
  payload: { badgeCode: string; nfcTagId: string },
  adminKey: string
): Promise<{ success: boolean; message: string; badgeCode: string; nfcTagId: string; attendee: any; theme: any }> {
  const res = await fetch(`${API_BASE}/api/production/nfc/pair`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAdminHeaders(adminKey),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to pair NFC tag', res.status, errorData);
  }
  return res.json();
}

export async function importNfcPairingCsv(
  payload: { csvContent?: string; pairings?: Array<{ badgeCode: string; nfcTagId: string }> },
  adminKey: string
): Promise<{ success: boolean; totalRows: number; pairedCount: number; errors?: string[] }> {
  const res = await fetch(`${API_BASE}/api/production/nfc/import-csv`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAdminHeaders(adminKey),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to import NFC CSV', res.status, errorData);
  }
  return res.json();
}

// ==========================================
// Phase 5: Participants Directory, Applications Inbox & Sponsor Pass Limits
// ==========================================

export interface ApplicationRecord {
  id: string;
  eventId?: string | null;
  type: 'speaker' | 'media' | 'student_innovator';
  status: 'pending' | 'approved' | 'rejected';
  applicantName: string;
  applicantEmail: string;
  applicantPhone?: string | null;
  organization?: string | null;
  jobTitle?: string | null;
  details?: Record<string, any> | null;
  assignedPod?: string | null;
  participantId?: string | null;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  rejectionReason?: string | null;
  createdAt: string;
}

export interface ParticipantRecord {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  companyName?: string | null;
  jobTitle?: string | null;
  avatarUrl?: string | null;
  primaryRole: string;
  roles: string[];
  tier: string;
  ticketType?: string | null;
  badgeStatus: string;
  checkedIn: boolean;
  checkedInAt?: string | null;
  collectionCode?: string | null;
  stallNumber?: string | null;
  assignedPod?: string | null;
  directoryOptOut: boolean;
  shareContactOptOut: boolean;
  badgeCode?: string | null;
  nfcTagId?: string | null;
  theme: {
    role: string;
    label: string;
    colorHex: string;
    textColorHex?: string;
    badgeClass: string;
    textClass: string;
    borderClass: string;
  };
  createdAt: string;
}

export interface SponsorLimitRecord {
  id: string;
  eventId?: string | null;
  sponsorTier: string;
  companyName?: string | null;
  vipPassLimit: number;
  delegatePassLimit: number;
  exhibitorPassLimit: number;
  visitorPassLimit: number;
  createdAt: string;
}

export interface SubmitApplicationPayload {
  type: 'speaker' | 'media' | 'student_innovator';
  applicantName: string;
  applicantEmail: string;
  applicantPhone?: string;
  organization?: string;
  jobTitle?: string;
  details?: Record<string, any>;
  assignedPod?: string;
}

export interface ManualParticipantPayload {
  fullName: string;
  email: string;
  phone: string;
  companyName?: string;
  jobTitle?: string;
  primaryRole: string;
  secondaryRoles?: string[];
  tier?: string;
  stallNumber?: string;
  assignedPod?: string;
  directoryOptOut?: boolean;
  shareContactOptOut?: boolean;
}

export interface UpdateParticipantPayload {
  fullName?: string;
  companyName?: string;
  jobTitle?: string;
  primaryRole?: string;
  secondaryRoles?: string[];
  stallNumber?: string;
  assignedPod?: string;
  directoryOptOut?: boolean;
  shareContactOptOut?: boolean;
}

export async function fetchApplications(
  adminKey: string,
  filters?: { type?: string; status?: string; search?: string }
): Promise<{ success: boolean; applications: ApplicationRecord[]; metrics: any }> {
  const params = new URLSearchParams();
  if (filters?.type && filters.type !== 'all') params.set('type', filters.type);
  if (filters?.status && filters.status !== 'all') params.set('status', filters.status);
  if (filters?.search) params.set('search', filters.search);

  const res = await fetch(`${API_BASE}/api/applications?${params.toString()}`, {
    headers: getAdminHeaders(adminKey),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch applications', res.status, errorData);
  }
  return res.json();
}

export async function approveApplication(
  adminKey: string,
  id: string,
  payload?: { assignedPod?: string; jobTitle?: string; organization?: string }
): Promise<{ success: boolean; message: string; badgeCode: string; application: ApplicationRecord }> {
  const res = await fetch(`${API_BASE}/api/applications/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAdminHeaders(adminKey),
    },
    body: JSON.stringify(payload || {}),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to approve application', res.status, errorData);
  }
  return res.json();
}

export async function rejectApplication(
  adminKey: string,
  id: string,
  reason: string
): Promise<{ success: boolean; message: string; application: ApplicationRecord }> {
  const res = await fetch(`${API_BASE}/api/applications/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAdminHeaders(adminKey),
    },
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to reject application', res.status, errorData);
  }
  return res.json();
}

export async function submitApplication(
  payload: SubmitApplicationPayload
): Promise<{ success: boolean; message: string; application: ApplicationRecord }> {
  const res = await fetch(`${API_BASE}/api/applications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to submit application', res.status, errorData);
  }
  return res.json();
}

export async function fetchAdminParticipants(
  adminKey: string,
  filters?: {
    search?: string;
    role?: string;
    tier?: string;
    badgeStatus?: string;
    checkedIn?: string;
    limit?: number;
    offset?: number;
  }
): Promise<{
  success: boolean;
  participants: ParticipantRecord[];
  pagination: { limit: number; offset: number; count: number };
  summary: { total: number; checkedIn: number; printed: number; byRole: Record<string, number> };
}> {
  const params = new URLSearchParams();
  if (filters?.search) params.set('search', filters.search);
  if (filters?.role && filters.role !== 'all') params.set('role', filters.role);
  if (filters?.tier && filters.tier !== 'all') params.set('tier', filters.tier);
  if (filters?.badgeStatus && filters.badgeStatus !== 'all') params.set('badgeStatus', filters.badgeStatus);
  if (filters?.checkedIn && filters.checkedIn !== 'all') params.set('checkedIn', filters.checkedIn);
  if (filters?.limit) params.set('limit', String(filters.limit));
  if (filters?.offset) params.set('offset', String(filters.offset));

  const res = await fetch(`${API_BASE}/api/admin/participants?${params.toString()}`, {
    headers: getAdminHeaders(adminKey),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch participants', res.status, errorData);
  }
  return res.json();
}

export async function createAdminParticipant(
  adminKey: string,
  payload: ManualParticipantPayload
): Promise<{ success: boolean; message: string; participant: ParticipantRecord }> {
  const res = await fetch(`${API_BASE}/api/admin/participants`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAdminHeaders(adminKey),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to create participant', res.status, errorData);
  }
  return res.json();
}

export async function updateAdminParticipant(
  adminKey: string,
  participantId: string,
  payload: UpdateParticipantPayload
): Promise<{ success: boolean; message: string; participant: any }> {
  const res = await fetch(`${API_BASE}/api/admin/participants/${encodeURIComponent(participantId)}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAdminHeaders(adminKey),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to update participant', res.status, errorData);
  }
  return res.json();
}

export async function fetchSponsorLimits(
  adminKey: string
): Promise<{ success: boolean; limits: SponsorLimitRecord[] }> {
  const res = await fetch(`${API_BASE}/api/admin/sponsors/limits`, {
    headers: getAdminHeaders(adminKey),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch sponsor limits', res.status, errorData);
  }
  return res.json();
}

export async function createSponsorLimit(
  adminKey: string,
  payload: {
    sponsorTier: string;
    companyName?: string;
    vipPassLimit: number;
    delegatePassLimit: number;
    exhibitorPassLimit: number;
    visitorPassLimit: number;
  }
): Promise<{ success: boolean; message: string; limit: SponsorLimitRecord }> {
  const res = await fetch(`${API_BASE}/api/admin/sponsors/limits`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAdminHeaders(adminKey),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to configure sponsor pass limit', res.status, errorData);
  }
  return res.json();
}

// ============================================================================
// PHASE 7: PERKS & MEAL VOUCHERS API CLIENT
// ============================================================================

export interface PerkItem {
  id: string;
  code: string;
  name: string;
  description?: string;
  perkType: 'event_pass' | 'daily_wallet';
  unitValueInr: number;
  validDate?: string | null;
  sponsorName?: string | null;
  sponsorTier?: string | null;
  maxRedemptions: number;
  redeemedCount: number;
  remainingRedemptions: number;
  status: 'available' | 'already_redeemed' | 'ineligible';
  ineligibilityReason?: string;
  lastRedemption?: {
    redeemedAt: string;
    vendorName: string;
    vendorCounterId: string;
  } | null;
}

export interface LookupPerksResponse {
  attendee: {
    id: string;
    fullName: string;
    companyName?: string | null;
    jobTitle?: string | null;
    email: string;
    passTier: string;
    badgeStatus: string;
    roles: string[];
    badgeCode: string | null;
    nfcTagId: string | null;
  };
  checkDate: string;
  perks: PerkItem[];
}

export interface RedeemPerkPayload {
  perkId: string;
  identifier: string;
  vendorCounterId: string;
  vendorName: string;
  redeemedByStaffId?: string;
  redemptionDate?: string;
  notes?: string;
}

export interface RedeemPerkResponse {
  success: boolean;
  redemptionId: string;
  perk: {
    id: string;
    code: string;
    name: string;
    unitValueInr: number;
    sponsorName?: string | null;
  };
  attendee: {
    id: string;
    fullName: string;
    companyName?: string | null;
  };
  vendor: {
    vendorName: string;
    vendorCounterId: string;
  };
  redeemedAt: string;
  remainingRedemptions: number;
}

export interface ParticipantPerk {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  perkType: string;
  unitValueInr: number;
  validDate?: string | null;
  sponsorName?: string | null;
  sponsorTier?: string | null;
  isRedeemed: boolean;
  redeemedCount: number;
  maxRedemptions: number;
  lastRedemption?: {
    redeemedAt: string;
    vendorName: string;
    vendorCounterId: string;
  } | null;
}

export interface PerksSettlementReport {
  eventId: string;
  eventName: string;
  totalRedemptions: number;
  totalSettlementInr: number;
  byVendor: Array<{
    vendorName: string;
    count: number;
    totalInr: number;
  }>;
  byPerk: Array<{
    code: string;
    name: string;
    sponsorName: string | null;
    count: number;
    totalInr: number;
  }>;
  byDate: Array<{
    date: string;
    count: number;
    totalInr: number;
  }>;
}

function getScannerOrAdminHeaders(scannerKey?: string): Record<string, string> {
  const key =
    scannerKey ||
    (typeof window !== 'undefined' ? sessionStorage.getItem('iiab_admin_key') || undefined : undefined);
  const headers: Record<string, string> = {};
  if (key) {
    headers['x-scanner-key'] = key;
    headers['Authorization'] = `Bearer ${key}`;
  }
  return headers;
}

export async function lookupBadgePerks(identifier: string, date?: string, scannerKey?: string): Promise<LookupPerksResponse> {
  const res = await fetch(`${API_BASE}/api/perks/lookup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getScannerOrAdminHeaders(scannerKey),
    },
    body: JSON.stringify({ identifier, date }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Lookup failed', res.status, errorData);
  }
  return res.json();
}

export async function redeemPerkVoucher(payload: RedeemPerkPayload, scannerKey?: string): Promise<RedeemPerkResponse> {
  const res = await fetch(`${API_BASE}/api/perks/redeem`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getScannerOrAdminHeaders(scannerKey),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Redemption failed', res.status, errorData);
  }
  return res.json();
}

export async function fetchParticipantPerks(attendeeId: string, token?: string): Promise<{ attendeeId: string; perks: ParticipantPerk[] }> {
  const res = await fetch(`${API_BASE}/api/participants/${encodeURIComponent(attendeeId)}/perks`, {
    headers: getPortalAuthHeaders(token),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch participant perks', res.status, errorData);
  }
  return res.json();
}

export async function fetchAdminPerks(adminKey: string): Promise<{ eventId: string; eventName: string; perks: any[] }> {
  const res = await fetch(`${API_BASE}/api/admin/perks`, {
    headers: getAdminHeaders(adminKey),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch admin perks', res.status, errorData);
  }
  return res.json();
}

export async function createAdminPerk(adminKey: string, payload: any): Promise<any> {
  const res = await fetch(`${API_BASE}/api/admin/perks`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAdminHeaders(adminKey),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to create perk', res.status, errorData);
  }
  return res.json();
}

export async function updateAdminPerk(adminKey: string, id: string, payload: any): Promise<any> {
  const res = await fetch(`${API_BASE}/api/admin/perks/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAdminHeaders(adminKey),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to update perk', res.status, errorData);
  }
  return res.json();
}

export async function fetchPerksSettlement(adminKey: string): Promise<PerksSettlementReport> {
  const res = await fetch(`${API_BASE}/api/admin/perks/settlement`, {
    headers: getAdminHeaders(adminKey),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch settlement report', res.status, errorData);
  }
  return res.json();
}

// ============================================================================
// PHASE 8: REPORTS & INTERACTION ANALYTICS API CLIENT
// ============================================================================

export interface InteractionReportsResponse {
  eventId: string | null;
  eventName: string;
  totalInteractions: number;
  byType: Array<{
    type: string;
    count: number;
    label: string;
  }>;
  qrVsNfc: {
    qrCount: number;
    nfcCount: number;
    qrPercentage: number;
    nfcPercentage: number;
  };
  funnel: {
    totalRegistered: number;
    badgeScanned: number;
    exhibitorConnected: number;
    meetingScheduled: number;
  };
  topPeople: Array<{
    attendeeId: string;
    fullName: string;
    companyName: string;
    jobTitle: string;
    tier: string;
    primaryRole: string;
    totalInteractions: number;
  }>;
  topStalls: Array<{
    boothId: string;
    companyName: string;
    count: number;
  }>;
  hourlyVelocity: Array<{
    hour: string;
    count: number;
  }>;
}

export interface AttendanceReportsResponse {
  eventId: string | null;
  eventName: string;
  totalRegistered: number;
  totalCheckedIn: number;
  turnoutRatePercent: number;
  uniqueAttendeesDay1: number;
  uniqueAttendeesDay2: number;
  totalHandovers: number;
  byRole: Array<{
    role: string;
    label: string;
    totalRegistered: number;
    checkedIn: number;
    attendancePercent: number;
  }>;
  byTier: Array<{
    tier: string;
    label: string;
    totalRegistered: number;
    checkedIn: number;
    attendancePercent: number;
  }>;
}

export async function fetchInteractionReports(adminKey: string): Promise<InteractionReportsResponse> {
  const res = await fetch(`${API_BASE}/api/admin/reports/interactions`, {
    headers: getAdminHeaders(adminKey),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch interaction reports', res.status, errorData);
  }
  return res.json();
}

export async function fetchAttendanceReports(adminKey: string): Promise<AttendanceReportsResponse> {
  const res = await fetch(`${API_BASE}/api/admin/reports/attendance`, {
    headers: getAdminHeaders(adminKey),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch attendance reports', res.status, errorData);
  }
  return res.json();
}

export function getInteractionsCsvDownloadUrl(): string {
  return `${API_BASE}/api/admin/reports/export/interactions.csv`;
}

export function getAttendanceCsvDownloadUrl(): string {
  return `${API_BASE}/api/admin/reports/export/attendance.csv`;
}

export async function downloadInteractionsCsv(adminKey: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/reports/export/interactions.csv`, {
    headers: getAdminHeaders(adminKey),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new ApiError(err?.message || 'Failed to export interaction logs', res.status, err);
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `iiab-interactions-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export async function downloadAttendanceCsv(adminKey: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/reports/export/attendance.csv`, {
    headers: getAdminHeaders(adminKey),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new ApiError(err?.message || 'Failed to export turnout roster', res.status, err);
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `iiab-turnout-roster-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

// ============================================================================
// PHASE 9: SYSTEM HEALTH & 3-FRONT ERROR RECOVERY API CLIENT
// ============================================================================

export interface AdminSystemHealthResponse {
  success: boolean;
  integrations: {
    razorpay: boolean;
    zohoBooks: boolean;
    amazonSes: boolean;
    gmailSmtp: boolean;
    whatsapp: boolean;
    supabase: boolean;
    inngest: boolean;
  };
  exceptions: {
    attendeesMissingInvoice: Array<{
      id: string;
      fullName: string;
      email: string;
      companyName?: string | null;
      amountPaid?: number | null;
      razorpayOrderId?: string | null;
      gstin?: string | null;
      createdAt?: string;
    }>;
    reservationsMissingInvoice: Array<{
      id: string;
      boothId: string;
      companyName: string;
      contactEmail: string;
      agreedAmountInr?: number | null;
      gstin?: string | null;
      createdAt?: string;
    }>;
    stuckPendingOrders: Array<{
      id: string;
      type: 'stall' | 'attendee';
      reference: string;
      email: string;
      companyName?: string | null;
      amountInr: number;
      createdAt?: string;
    }>;
  };
  counts: {
    attendeesMissingInvoiceCount: number;
    reservationsMissingInvoiceCount: number;
    stuckPendingOrdersCount: number;
    totalActiveAlerts: number;
  };
}

export async function fetchAdminSystemHealth(adminKey: string): Promise<AdminSystemHealthResponse> {
  const res = await fetch(`${API_BASE}/api/admin/system-health`, {
    headers: getAdminHeaders(adminKey),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch system health', res.status, errorData);
  }
  return res.json();
}

export interface IntegrationTestResult {
  success: boolean;
  service?: string;
  provider: string;
  status: 'ok' | 'connected' | 'unconfigured' | 'error';
  message: string;
  details?: Record<string, any>;
  latencyMs: number;
}

export async function testAdminIntegration(
  adminKey: string,
  payload: { provider: string; recipientEmail?: string }
): Promise<IntegrationTestResult> {
  const res = await fetch(`${API_BASE}/api/admin/system-health/test-integration`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAdminHeaders(adminKey),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to test integration', res.status, errorData);
  }
  return res.json();
}

export async function retryAttendeeZohoInvoice(
  adminKey: string,
  attendeeId: string,
  payload?: { gstin?: string; email?: string }
): Promise<{ success: boolean; message?: string; zohoInvoiceId?: string; zohoInvoiceNumber?: string }> {
  const res = await fetch(`${API_BASE}/api/admin/attendees/${encodeURIComponent(attendeeId)}/retry-invoice`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAdminHeaders(adminKey),
    },
    body: JSON.stringify(payload || {}),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to regenerate Zoho GST invoice', res.status, errorData);
  }
  return res.json();
}

export async function retryReservationZohoInvoice(
  adminKey: string,
  reservationId: string,
  payload?: { gstin?: string; contactEmail?: string }
): Promise<{ success: boolean; message?: string; zohoInvoiceId?: string; zohoInvoiceNumber?: string }> {
  const res = await fetch(`${API_BASE}/api/admin/reservations/${encodeURIComponent(reservationId)}/retry-invoice`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAdminHeaders(adminKey),
    },
    body: JSON.stringify(payload || {}),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to regenerate stall Zoho GST invoice', res.status, errorData);
  }
  return res.json();
}

export async function syncOrderWithRazorpay(
  adminKey: string,
  orderId: string,
  payload?: { type?: 'stall' | 'attendee'; paymentId?: string }
): Promise<{ success: boolean; message: string; status?: string }> {
  const res = await fetch(`${API_BASE}/api/admin/orders/${encodeURIComponent(orderId)}/sync-razorpay`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAdminHeaders(adminKey),
    },
    body: JSON.stringify(payload || {}),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to sync payment status', res.status, errorData);
  }
  return res.json();
}

export async function resendAttendeeConfirmationEmail(
  adminKey: string,
  attendeeId: string,
  payload?: { email?: string }
): Promise<{ success: boolean; message: string; email?: string }> {
  const res = await fetch(`${API_BASE}/api/admin/attendees/${encodeURIComponent(attendeeId)}/resend-confirmation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAdminHeaders(adminKey),
    },
    body: JSON.stringify(payload || {}),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to resend confirmation email', res.status, errorData);
  }
  return res.json();
}

export async function resendReservationConfirmationEmail(
  adminKey: string,
  reservationId: string,
  payload?: { email?: string }
): Promise<{ success: boolean; message: string; email?: string }> {
  const res = await fetch(`${API_BASE}/api/admin/reservations/${encodeURIComponent(reservationId)}/resend-confirmation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAdminHeaders(adminKey),
    },
    body: JSON.stringify(payload || {}),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to resend stall confirmation email', res.status, errorData);
  }
  return res.json();
}

export interface OfflineManifestEntry {
  attendeeId: string;
  credentialId: string | null;
  badgeCode: string | null;
  allBadgeCodes: string[];
  ReplacedBadgeCodes: string[];
  nfcTagId: string | null;
  qrToken: string | null;
  collectionCode: string | null;
  fullName: string;
  companyName: string | null;
  jobTitle: string | null;
  email: string;
  tier: string;
  primaryRole: string;
  roles: string[];
  ticketType: string | null;
  avatarUrl: string | null;
  paymentStatus: string;
  isUnpaid: boolean;
  checkedInAt: string | null;
  theme: {
    label: string;
    bandColor: string;
    textColor: string;
    accentColor: string;
  };
}

export interface OfflineBadgeManifestResponse {
  success: boolean;
  generatedAt: string;
  totalBadges: number;
  entries: OfflineManifestEntry[];
  perks: any[];
  redemptions: Array<{
    id: string;
    perkId: string;
    attendeeId: string;
    redemptionDate: string;
    vendorName: string;
    vendorCounterId: string;
    redeemedAt: string;
  }>;
}

export async function fetchOfflineBadgeManifest(scannerKey?: string): Promise<OfflineBadgeManifestResponse> {
  const effectiveKey =
    scannerKey ||
    (typeof window !== 'undefined' ? sessionStorage.getItem('iiab_admin_key') || undefined : undefined);
  const res = await fetch(`${API_BASE}/api/checkin/offline-manifest`, {
    headers: effectiveKey ? getAdminHeaders(effectiveKey) : {},
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to fetch offline badge manifest', res.status, errorData);
  }
  return res.json();
}

export async function syncOfflineGateScansBatch(
  scans: Array<{
    clientScanId?: string;
    code: string;
    location?: string;
    scanType?: 'door_entry' | 'hall_entry' | 'keynote_entry';
    eventDay?: 'build_up' | 'day1' | 'day2' | 'dismantle';
    scannedAt?: string;
  }>,
  scannerKey?: string
): Promise<{ success: boolean; syncedCount: number; totalReceived: number; results: any[] }> {
  const effectiveKey =
    scannerKey ||
    (typeof window !== 'undefined' ? sessionStorage.getItem('iiab_admin_key') || undefined : undefined);
  const res = await fetch(`${API_BASE}/api/checkin/sync-batch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(effectiveKey ? getAdminHeaders(effectiveKey) : {}),
    },
    body: JSON.stringify({ scans }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to sync offline gate scans', res.status, errorData);
  }
  return res.json();
}

export async function syncOfflinePerkRedemptionsBatch(
  redemptions: Array<{
    clientRedemptionId?: string;
    perkId: string;
    identifier: string;
    vendorCounterId: string;
    vendorName: string;
    redemptionDate?: string;
    notes?: string;
  }>,
  scannerKey?: string
): Promise<{ success: boolean; syncedCount: number; conflictsCount: number; results: any[] }> {
  const effectiveKey =
    scannerKey ||
    (typeof window !== 'undefined' ? sessionStorage.getItem('iiab_admin_key') || undefined : undefined);
  const res = await fetch(`${API_BASE}/api/perks/sync-batch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(effectiveKey ? getAdminHeaders(effectiveKey) : {}),
    },
    body: JSON.stringify({ redemptions }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to sync offline perk redemptions', res.status, errorData);
  }
  return res.json();
}

export async function sendAutomationPreviewDigest(
  adminKey: string,
  payload: {
    type: 'pre_event_countdown' | 'exhibitor_daily_digest';
    stage?: 'T-7' | 'T-24H' | 'DAY1_MORNING';
    toEmail?: string;
  }
): Promise<{ success: boolean; message: string; targetEmail: string }> {
  const res = await fetch(`${API_BASE}/api/admin/automations/preview-digest`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAdminHeaders(adminKey),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to send preview digest', res.status, errorData);
  }
  return res.json();
}

export async function requestPortalAccountDeletion(
  payload: { reason?: string },
  email?: string,
  token?: string
): Promise<{
  success: boolean;
  status: string;
  requestedAt: string;
  message: string;
}> {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (token) params.set('token', token);
  const query = params.toString();

  const res = await fetch(
    `${API_BASE}/api/portal/account-deletion-request${query ? `?${query}` : ''}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getPortalAuthHeaders(token),
      },
      body: JSON.stringify(payload || {}),
    }
  );
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(
      errorData?.message || 'Failed to submit account deletion request',
      res.status,
      errorData
    );
  }
  return res.json();
}

export async function registerPortalPushToken(
  payload: { token: string; platform?: 'ios' | 'android' | 'web' },
  email?: string,
  portalToken?: string
): Promise<{
  success: boolean;
  registeredTokensCount: number;
  platform: string;
  updatedAt: string;
}> {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (portalToken) params.set('token', portalToken);
  const query = params.toString();

  const res = await fetch(
    `${API_BASE}/api/portal/push-tokens${query ? `?${query}` : ''}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getPortalAuthHeaders(portalToken),
      },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(errorData?.message || 'Failed to register push token', res.status, errorData);
  }
  return res.json();
}

export async function syncOfflineConnectionsBatch(
  scans: Array<{
    badgeCode: string;
    note?: string;
    scannedAt?: string;
    method?: 'qr' | 'nfc';
  }>,
  email?: string,
  portalToken?: string
): Promise<{
  success: boolean;
  syncedCount: number;
  connections: Array<{ badgeCode: string; fullName: string; companyName: string }>;
}> {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (portalToken) params.set('token', portalToken);
  const query = params.toString();

  const res = await fetch(
    `${API_BASE}/api/portal/connections/sync-batch${query ? `?${query}` : ''}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getPortalAuthHeaders(portalToken),
      },
      body: JSON.stringify({ scans }),
    }
  );
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(
      errorData?.message || 'Failed to sync offline B2B connections',
      res.status,
      errorData
    );
  }
  return res.json();
}

export async function syncOfflineExhibitorLeadsBatch(
  portalToken: string,
  leads: Array<{
    badgeCode: string;
    leadRating?: 'hot' | 'warm' | 'cold';
    notes?: string;
    scannedAt?: string;
  }>
): Promise<{
  success: boolean;
  syncedCount: number;
  leads: Array<{ badgeCode: string; fullName: string; companyName: string; tier: string }>;
}> {
  const res = await fetch(`${API_BASE}/api/exhibitors/leads/sync-batch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-portal-token': portalToken,
    },
    body: JSON.stringify({ portalToken, leads }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new ApiError(
      errorData?.message || 'Failed to sync offline exhibitor leads',
      res.status,
      errorData
    );
  }
  return res.json();
}



